/**
 * The draft board on a phone — one round at a time, per the canvas's Turn 12.
 *
 * TEN COLUMNS DO NOT FIT A PHONE, AND SHRINKING THEM IS THE WRONG ANSWER. The desktop grid is
 * the right object at 1400px and an unreadable one at 390: at that width a cell is 32px, which
 * is not enough for a surname, a club chip and a bye, so every cell would lose the information
 * the grid exists to carry. The drawing's answer is to change the object rather than the scale —
 * a round is a LIST of ten rows, nothing is cut, and nothing scrolls sideways.
 *
 * TIM'S CALL, 2026-08-17: Turn 12's design, over `8b`'s pinch-zoomable grid. The trade is real
 * and worth stating — a GM who wants the whole board at once no longer has it on his phone, and
 * the drawing's own note says as much ("a GM who wants the whole board has a laptop open"). What
 * he gets instead is every row legible on the night, which is the failure that actually costs
 * somebody a pick.
 *
 * The pager opens on the round on the clock, because that is where the room is.
 */

import { useEffect, useState } from 'react';
import { managerShort } from '../domain/identity.js';
import { pickCode, type BoardPick } from '../domain/board.js';
import { nflInfoOf } from './NflChip.js';
import { shortSurname, splitName } from './BoardGrid.js';
import { PickClock } from './PickClock.js';

interface Props {
  board: readonly BoardPick[];
  rounds: number;
  teams: number;
  clock: BoardPick | null;
  me?: string;
  onPick: (overall: number) => void;
  canEnterPick: (p: BoardPick) => boolean;
  /** Picks whose clock ran out and which are still the owner's to make. */
  lapsed?: ReadonlySet<number>;
  /** When the last pick landed, and the allowance for the round on the clock (17e's clock words). */
  since?: string | null;
  seconds?: number | null;
  startedAt?: ReadonlyMap<number, string>;
  /** The instant the clocks read while the room is stopped. */
  clockNow?: number | null;
}

export function PhoneBoard(
  { board, rounds, teams, clock, me, onPick, canEnterPick, lapsed, since, seconds, startedAt,
    clockNow = null }: Props,
) {
  const [round, setRound] = useState(clock?.round ?? 1);

  /*
   * Follow the room, but not over the reader's shoulder.
   *
   * The pager jumps to the round on the clock when that round CHANGES — the draft moving on is
   * worth following. It does not jump on every re-render, or a manager looking back at round two
   * would be thrown forward the moment anybody picked.
   */
  useEffect(() => { if (clock?.round) setRound(clock.round); }, [clock?.round]);

  const rows = board.filter((p) => p.round === round).sort((a, b) => a.overall - b.overall);
  const made = rows.filter((p) => p.player).length;

  return (
    <div className="pboard">
      <div className="ppager">
        <button type="button" className="linkbtn" disabled={round <= 1}
          aria-label="Previous round" onClick={() => setRound((r) => Math.max(1, r - 1))}>‹</button>
        <span className="pround">
          <b>Round {round}</b>
          <span className="mut dir">{round % 2 === 0 ? '← even' : 'odd →'}</span>
          <span className="mut">{made} of {teams} in</span>
        </span>
        <button type="button" className="linkbtn" disabled={round >= rounds}
          aria-label="Next round" onClick={() => setRound((r) => Math.min(rounds, r + 1))}>›</button>
      </div>

      <ul className="prows">
        {rows.map((p) => {
          const onClock = clock?.overall === p.overall;
          const late = lapsed?.has(p.overall) ?? false;
          const mine = p.owner.toUpperCase() === (me ?? '').toUpperCase();
          const canEnter = canEnterPick(p);
          const state = p.player ? (p.keeper ? 'keeper' : p.farm ? 'farm' : 'made')
            : late ? 'late' : onClock ? 'clock' : 'empty';

          return (
            <li key={p.overall} className={`prow ${state} ${mine ? 'mine' : ''}`}>
              <button type="button" className="prowbtn"
                disabled={Boolean(p.player) || !canEnter}
                title={p.player
                  ? `${pickCode(p, teams)} · ${managerShort(p.owner)}`
                  : canEnter ? `Enter ${pickCode(p, teams)}` : `${managerShort(p.owner)}'s pick`}
                onClick={() => onPick(p.overall)}>
                <span className="pcode">{pickCode(p, teams)}</span>
                <span className="powner">{managerShort(p.owner)}</span>
                <span className="pmain">
                  {p.player ? (
                    <>
                      <b className="pname">
                        {(() => { const { last, first } = splitName(p.player); return (
                          <><span className="sur">{shortSurname(last)}</span>{first ? ` ${first}` : ''}</>
                        ); })()}
                      </b>
                      <span className="pmeta">
                        {[p.pos, nflInfoOf(p.player)?.team ?? p.team].filter(Boolean).join(' · ')}
                        {p.keeper && <span className="chip tiny">{p.farm ? 'farm' : 'keeper'}</span>}
                      </span>
                    </>
                  ) : late ? (
                    /* 17e: the lapsed row says it is still his, and how long over. */
                    <span className="pwho late">
                      lapsed · still open
                      {(startedAt?.get(p.overall) ?? since) && <PickClock now={clockNow ?? undefined} since={startedAt?.get(p.overall) ?? since!} seconds={null} size="s" skin="light" className="plate" />}
                    </span>
                  ) : onClock ? (
                    <span className="pwho clock">
                      on the clock
                      {since && seconds != null
                        && <PickClock now={clockNow ?? undefined} since={since} seconds={seconds} size="s" skin="light" className="plate" />}
                    </span>
                  ) : (
                    <span className="pwho"><span className="mut">—</span></span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
